import { useTheme } from "@mui/material/styles";

/**
 * Hook that returns MRT surface props with resolved theme colors.
 * MRT v2's internal `getMRTTheme` computes `baseBackgroundColor` from the MUI
 * palette and applies it to toolbars and the sticky thead via Emotion inline
 * styles — these beat `sx` token strings. Passing `mrtTheme.baseBackgroundColor`
 * with the real resolved hex is the only reliable override.
 */
export function useMrtSurfaceProps() {
  const theme = useTheme();
  const paperBg = theme.palette.background.paper;
  const defaultBg = theme.palette.background.default;

  return {
    mrtTheme: { baseBackgroundColor: paperBg },
    muiTablePaperProps: {
      elevation: 0,
      sx: { backgroundColor: paperBg, border: 1, borderColor: "divider" },
    },
    muiTopToolbarProps: {
      sx: { backgroundColor: paperBg, borderBottom: 1, borderColor: "divider" },
    },
    muiBottomToolbarProps: {
      sx: { backgroundColor: paperBg, borderTop: 1, borderColor: "divider" },
    },
    muiTableContainerProps: { sx: { backgroundColor: paperBg } },
    muiTableHeadProps: { sx: { opacity: 1, backgroundColor: defaultBg } },
    muiTableHeadRowProps: { sx: { backgroundColor: defaultBg } },
  };
}

/** Static fallback — use useMrtSurfaceProps() hook in components instead */
export const mrtSurfaceProps = {
  muiTablePaperProps: {
    elevation: 0,
    sx: { backgroundColor: "background.paper", border: 1, borderColor: "divider" },
  },
  muiTopToolbarProps: {
    sx: { backgroundColor: "background.paper", borderBottom: 1, borderColor: "divider" },
  },
  muiBottomToolbarProps: {
    sx: { backgroundColor: "background.paper", borderTop: 1, borderColor: "divider" },
  },
  muiTableContainerProps: { sx: { backgroundColor: "background.paper" } },
  muiTableHeadProps: { sx: { opacity: 1 } },
  muiTableHeadRowProps: { sx: { backgroundColor: "background.default" } },
};

export const mrtHeadCellSx = {
  backgroundColor: "background.default",
  borderColor: "divider",
  color: "text.primary",
};

export const mrtBodyCellSx = {
  borderColor: "divider",
  color: "text.primary",
};

export const mrtFilterHeadCellSx = {
  backgroundColor: "background.default",
  borderColor: "divider",
};

// Compact variant — mirrors compactTableProps but fully themed
export const mrtCompactProps = {
  ...mrtSurfaceProps,
  muiTableHeadCellProps: {
    sx: {
      ...mrtHeadCellSx,
      borderRight: "1px solid",
      borderTop: "1px solid",
      "& .Mui-TableHeadCell-Content-Wrapper": {
        whiteSpace: "normal",
        justifyContent: "center",
        marginBottom: "0.4px",
      },
      fontSize: "0.7rem",
      verticalAlign: "middle",
      height: "95px",
      padding: "2px",
    },
  },
  muiTableProps: {
    size: "small",
    sx: {
      "& .MuiInputBase-root": { fontSize: "0.7rem", borderRadius: 0 },
      "& .MuiButtonBase-root": { fontSize: "0.7rem" },
      "& .MuiTableCell-root": { fontSize: "0.7rem" },
    },
  },
  muiTableBodyCellProps: {
    sx: {
      ...mrtBodyCellSx,
      textAlign: "center",
      borderRight: "1px solid",
      padding: "0rem",
      height: "33.1px",
    },
  },
  initialState: { density: "compact" },
};

// Normal variant — mirrors normalTableProps but fully themed
export const mrtNormalProps = {
  ...mrtSurfaceProps,
  muiTableHeadCellProps: {
    sx: {
      ...mrtHeadCellSx,
      borderRight: "1px solid",
      borderTop: "1px solid",
      "& .Mui-TableHeadCell-Content-Wrapper": {
        whiteSpace: "normal",
        justifyContent: "center",
      },
      fontSize: "0.8rem",
      verticalAlign: "middle",
    },
  },
  muiTableProps: {
    size: "small",
    sx: {
      "& .MuiInputBase-root": { fontSize: "0.8rem", borderRadius: 0 },
      "& .MuiButtonBase-root": { fontSize: "0.8rem" },
      "& .MuiTableCell-root": { fontSize: "0.8rem" },
    },
  },
  muiTableBodyCellProps: {
    sx: {
      ...mrtBodyCellSx,
      height: "32.4px",
    },
  },
};

// Minimal variant — mirrors minimalMuiTableProps but fully themed
export const mrtMinimalProps = {
  enableSorting: false,
  ...mrtSurfaceProps,
  muiTablePaperProps: {
    elevation: 0,
    sx: { backgroundColor: "transparent" },
  },
  muiTopToolbarProps: {
    sx: { backgroundColor: "transparent", mb: 1 },
  },
  muiTableProps: {
    sx: { border: "1px solid", borderColor: "divider" },
  },
  muiTableBodyProps: {
    sx: { backgroundColor: "background.paper" },
  },
  muiTableHeadCellProps: {
    sx: {
      ...mrtHeadCellSx,
      border: "1px solid",
      padding: 1,
      fontSize: 10,
    },
  },
  muiBottomToolbarProps: {
    sx: {
      border: "1px solid",
      borderColor: "divider",
      padding: 0,
    },
  },
  muiTableBodyCellProps: {
    sx: {
      ...mrtBodyCellSx,
      border: "1px solid",
      py: 0.3,
      px: 1,
    },
  },
  muiTableFooterCellProps: {
    sx: { padding: "5px" },
  },
};

// Standard tableProps variant — mirrors tableProps but fully themed
export const mrtStandardProps = {
  ...mrtSurfaceProps,
  muiTableHeadCellProps: {
    sx: {
      ...mrtHeadCellSx,
      borderRight: "1px solid",
      borderTop: "1px solid",
      "& .Mui-TableHeadCell-Content-Wrapper": {
        whiteSpace: "normal",
        justifyContent: "center",
        marginBottom: "0.4px",
      },
      fontSize: "12px",
      verticalAlign: "middle",
      height: "40.4px",
    },
  },
  muiTableProps: {
    size: "small",
    sx: {
      "& .MuiInputBase-root": { fontSize: "0.7rem", borderRadius: 0 },
      "& .MuiButtonBase-root": { fontSize: "0.7rem" },
    },
  },
  muiTableBodyCellProps: {
    sx: {
      ...mrtBodyCellSx,
      borderRight: "1px solid",
      padding: "0rem",
      paddingLeft: "5px",
    },
  },
};
